/**
 * Various DOM helpers.
 */

import { dashedToCamelCase } from './string';

// Cached regexes
const REGEX = {
  onlyNumber: /^-?\d+$/,
  // Also make sure the number of periods are exactly one if testing for float
  onlyNumberWithPeriods: /^-?[\d.]+$/,
};

/**
 * Parse an HTML string to DOM nodes.
 *
 * Extracted and tweaked version of parseHTML from jQuery 2.1.3, without
 * handling of script tags. Changed to return a DocumentFragment instead
 * of an array.
 *
 * Grabbed from https://gist.github.com/Munawwar/6e6362dbdf77c7865a99.
 *
 * A much simpler solution would be using a template element:
 *
 *     function parseHTML(htmlString) {
 *       const tmp = document.createElement('template');
 *       tmp.innerHTML = htmlString.trim();
 *       return template.content;
 *     }
 *
 * This will be available when IE11 support is no longer relevant.
 *
 * @param {string} elem - A string containing HTML.
 * @return {DocumentFragment}
 */
export const parseHTML = (function parseHTMLWrap() {
  const xhtmlTagRegex = /<(?!area|br|col|embed|hr|img|input|link|meta|param)(([\w:]+)[^>]*)\/>/gi;
  const tagNameRegex = /<([\w:]+)/;
  const htmlRegex = /<|&#?\w+;/;

  // We have to close these tags to support XHTML (#13200)
  const wrapMap = {
    // Support: IE9
    option: [1, "<select multiple='multiple'>", '</select>'],

    thead: [1, '<table>', '</table>'],
    col: [2, '<table><colgroup>', '</colgroup></table>'],
    tr: [2, '<table><tbody>', '</tbody></table>'],
    td: [3, '<table><tbody><tr>', '</tr></tbody></table>'],

    _default: [0, '', ''],
  };

  return function parseHTMLParser(elem) {
    let tmp;
    let tag;
    let wrap;
    let j;
    const fragment = document.createDocumentFragment();

    if (!htmlRegex.test(elem)) {
      // Convert non-HTML into a text node
      fragment.appendChild(document.createTextNode(elem));
    } else {
      // Convert HTML into DOM nodes
      tmp = fragment.appendChild(document.createElement('div'));

      // Deserialize a standard representation
      tag = (tagNameRegex.exec(elem) || ['', ''])[1].toLowerCase();
      wrap = wrapMap[tag] || wrapMap._default;
      tmp.innerHTML =
        wrap[1] + elem.replace(xhtmlTagRegex, '<$1></$2>') + wrap[2];

      // Descend through wrappers to the right content
      [j] = wrap;
      // Changing `j--` to `j -= 1` changes semantics somehow and leads to a
      // TypeError where tmp is null.
      // eslint-disable-next-line no-cond-assign, no-plusplus
      while (j--) {
        tmp = tmp.lastChild;
      }

      // Remove wrappers and append created nodes to fragment
      fragment.removeChild(fragment.firstChild);
      while (tmp.firstChild) {
        fragment.appendChild(tmp.firstChild);
      }
    }

    return fragment;
  };
}());

// IE11 is missing support for some. Filter out falsy values.
const NODE_COLLECTION_PROTOTYPES = [
  window.HTMLCollection && HTMLCollection.prototype,
  window.HTMLFormControlsCollection && HTMLFormControlsCollection.prototype,
  window.HTMLOptionsCollection && HTMLOptionsCollection.prototype,
  window.NodeList && NodeList.prototype,
].filter(Boolean);

/**
 * Check if an object is an `Element` DOM node.
 *
 * @param {*} obj - Element or whatever to check.
 * @return {boolean}
 */
export function isNode(obj) {
  // Theoretically vulnerable to any plain objects that has a `nodeType`
  // property to 1, but that is just doing weird stuff so it shouldn't be
  // a real problem.
  return typeof obj === 'object' && obj.nodeType === 1;
}

/**
 * Check if something is a collection of elements.
 *
 * Can either be an array of nodes or a proper collection like `NodeList`.
 *
 * @param {*} el - Element or whatever to check.
 * @return {boolean}
 */
export function isNodeCollection(el) {
  /* eslint-disable max-len */
  return (
    (Array.isArray(el) && el.every(isNode)) ||
    NODE_COLLECTION_PROTOTYPES.some((proto) =>
      Object.prototype.isPrototypeOf.call(proto, el),
    )
  );
}

/* ========================================================================= *\
    Data attributes
\* ========================================================================= */

const DATA_VALUE_MAP = {
  true: true,
  false: false,
  null: null,
  NaN,
};

/**
 * Save data on a DOM element data attribute.
 *
 * @param {Object} el - Element to save data on.
 * @param {string} key - Data key, will result in the attribute `data-[key]`.
 *   Note that the key can be camelCase, but the resulting attribute will
 *   always display as dash-case in the DOM tree.
 * @param {*} val - Value to save, will be converted to a string. Behaves
 *   like the `String` constructor, so false and null will for example be
 *   converted to 'false' and 'null' respectively.
 * @param {Function} [valueEncoder] - Value encoder. Will get the raw value
 *   as a parameter and must return a string. This should only be required
 *   for custom serialization of complex types like objects that can't be
 *   converted to a useful string automatically.
 * @example
 *
 * setData(el, 'count', 3);
 *
 * // Save an object
 * setData(el, 'someData', { this: 'is', complex: 33 }, JSON.stringify);
 */
export function setData(el, key, val, valueEncoder) {
  // Assigning to the DOM node, not the parameter as such
  /* eslint-disable no-param-reassign */
  const encodedVal = valueEncoder ? valueEncoder(val) : val;
  el.dataset[dashedToCamelCase(key)] = encodedVal;
}

/**
 * Read data from a DOM element data attribute.
 *
 * @param {Object} el - Element to read data from.
 * @param {string} key - Data key. Can be dash-case or the camelCase
 *   equivalent of it; `data-test` requires 'test' as the key while
 *   `data-other-test` can be 'other-test' or 'otherTest'.
 * @param {string|Function} [valueParser] - Value parser, can either be a
 *   predefined one named with a string or a custom function. If passing a
 *   function, it will get the raw string value as a parameter and should
 *   return the desired result.
 * @return {mixed} Raw or parsed value.
 * @example
 *
 * // Most numbers are automatically parsed. Use the Number constructor as a
 * // parser to convert the value to a number when that's not the case.
 * getData(el, 'serial-number', Number); // '00123'
 * // => 123
 *
 * // Keys in dash-case are handled, and some string values like 'false' are
 * // parsed to avoid something like Boolean('false') -> true.
 * getData(el, 'is-active');
 * // => false
 *
 * // Reading an object that used JSON.stringify as encoder
 * getData(el, 'someData', JSON.parse);
 * // => { this: 'is', complex: 33 }
 */
export function getData(el, key, valueParser) {
  const rawVal = el.dataset[dashedToCamelCase(key)];

  // Empty values are represented with null
  if (rawVal === '' || rawVal === undefined || rawVal === 'undefined') {
    return null;
  }

  // Handle stringified data types like 'false' and 'null'
  if (typeof DATA_VALUE_MAP[rawVal] !== 'undefined') {
    return DATA_VALUE_MAP[rawVal];
  }

  // If a custom parser is passed, let it do the work
  if (valueParser) {
    return valueParser(rawVal);
  }

  // Automatically parse numbers
  if (
    // Abort if starting with zero, to avoid converting something like
    // 0012 to 12.
    (rawVal === '0' || rawVal[0] !== '0') &&
    // Only digits...
    (REGEX.onlyNumber.test(rawVal) ||
      // ...or digits and a single priod
      (REGEX.onlyNumberWithPeriods.test(rawVal) &&
        (rawVal.match(/\./g) || []).length === 1))
  ) {
    return Number(rawVal);
  }

  return rawVal;
}

/**
 * Remove a DOM element data attribute.
 *
 * @param {Object} el - Element to read data from.
 * @param {string} key - Data key. Can be dash-case or the camelCase
 *   equivalent of it; `data-test` will use 'test' as the key while
 *   `data-other-test` can be 'other-test' or 'otherTest'.
 */
export function removeData(el, key) {
  delete el.dataset[dashedToCamelCase(key)];
}

/* ========================================================================= *\
    Element selection
\* ========================================================================= */

/**
 * Get DOM nodes that match a selector.
 *
 * @ignore
 * @param {Object} base - Element to select from, the result will be
 *   decendants of this.
 * @param {string} selector - Element selector.
 * @param {boolean} asNodeList - Return the NodeList instead of converting
 *   the result to an array.
 * @return {Array.<HTMLElement> | NodeList} Found element or null.
 */
function _select(base, selector, asNodeList = false) {
  const found = base.querySelectorAll(selector);
  return asNodeList ? found : Array.from(found);
}

/**
 * Get a single DOM node that matches a selector.
 *
 * @ignore
 * @param {Object} base - Element to select from, the result will be a
 *   decendant of this.
 * @param {string} selector - Element selector.
 * @return {?HTMLElement} Found element or null.
 */
function _selectSingle(base, selector) {
  return base.querySelector(selector) || null;
}

/**
 * Get nodes contained anywhere inside the document.
 *
 * @param {string} selector - Element selector.
 * @param {boolean} asNodeList - Return the live NodeList instead of
 *   converting the result to an array.
 * @return {Array.<HTMLElement> | NodeList} Found element or null.
 */
export function select(selector, asNodeList = false) {
  return _select(document, selector, asNodeList);
}

/**
 * Get a single node contained anywhere inside the document.
 *
 * @param {string} selector - Element selector.
 * @return {?HTMLElement} Found element or null.
 */
export function selectSingle(selector) {
  return _selectSingle(document, selector);
}

/**
 * Get nodes contained inside the specified node.
 *
 * @param {Object} base - Element to select from.
 * @param {string} selector - Element selector.
 * @param {boolean} asNodeList - Return the live NodeList instead of
 *   converting the result to an array.
 * @return {Array.<HTMLElement> | NodeList} Found element or null.
 */
export function selectFrom(base, selector, asNodeList = false) {
  return _select(base, selector, asNodeList);
}

/**
 * Get a single node contained inside the specified node.
 *
 * @param {Object} base - Element to select from.
 * @param {string} selector - Element selector.
 * @return {?HTMLElement} Found element or null.
 */
export function selectSingleFrom(base, selector) {
  return _selectSingle(base, selector);
}

/* ========================================================================= *\
    New node insertion
\* ========================================================================= */

/**
 * Insert a new element as a sibling before a target.
 *
 * @param {Object} target - Node to insert the new element before.
 * @param {Object} newElement - Element to insert.
 * @example
 *
 * insertBefore(formNode, parseHTML(`<p class="error">${errorMessage}</p>`));
 */
export function insertBefore(target, newElement) {
  target.parentNode.insertBefore(newElement, target);
}

/**
 * Insert a new element as a sibling after a target.
 *
 * @param {Object} target - Node to insert the new element after.
 * @param {Object} newElement - Element to insert.
 * @example
 *
 * insertAfter(list.lastChild, parseHTML(newListItems));
 */
export function insertAfter(target, newElement) {
  target.parentNode.insertBefore(newElement, target.nextSibling);
}

/* ========================================================================= *\
    Dimensions
\* ========================================================================= */

/**
 * Get the window's width or height.
 *
 * @param {string} name - `Width` or `Height`, NOTE CAPITAL FIRST LETTER.
 * @return {number}
 */
function getWindowDimension(name) {
  // https://github.com/jquery/jquery/blob/master/src/dimensions.js
  // Behave like jQuery's outerWidth and include scroll bars
  // https://github.com/jquery/jquery/issues/1729
  return document.documentElement[`client${name}`];
}

/**
 * Get the document's width or height.
 *
 * @param {string} name - `Width` or `Height`, NOTE CAPITAL FIRST LETTER.
 * @return {number}
 */
function getDocumentDimension(name) {
  const doc = document;

  // https://github.com/jquery/jquery/blob/master/src/dimensions.js
  return Math.max(
    doc.body[`scroll${name}`],
    doc.documentElement[`scroll${name}`],
    doc.body[`offset${name}`],
    doc.documentElement[`offset${name}`],
    doc.documentElement[`client${name}`],
  );
}

/**
 * Get the current window width.
 *
 * @return {number}
 */
export function getWindowWidth() {
  return getWindowDimension('Width');
}

/**
 * Get the current window height.
 *
 * @return {number}
 */
export function getWindowHeight() {
  return getWindowDimension('Height');
}

/**
 * Get the current document width.
 *
 * @return {number}
 */
export function getDocumentWidth() {
  return getDocumentDimension('Width');
}

/**
 * Get the current document height.
 *
 * @return {number}
 */
export function getDocumentHeight() {
  return getDocumentDimension('Height');
}
